// The start screen of a practice game: how many exercises its bank has for this level and filter (up to ५००), every
// numbered set of 10 with the learner's best score, and a button for the next set to play. Questions inside a set come
// in a new random order on every play.

import type { FC, ReactNode } from 'react'
import type { BankInfo, BankResponse, Level, PracticeMode, SetRecord, Unavailable } from '@/prakrit/components/practice/practice-types'

import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Check, Dices, LoaderCircle, Lock, Play, Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePrefs } from '@/lib/client/prefs'
import { useAudio } from '@/prakrit/lib/client/audio'
import { useSetProgress } from '@/prakrit/lib/client/practice-sets'
import { useSession } from '@/lib/client/session'
import { Bi, Eyebrow } from '@/prakrit/components/common/bi'
import { Notice, PRIMARY_BUTTON, SECONDARY_BUTTON, hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { LEVEL_INFO, modeInfo } from '@/prakrit/components/practice/practice-catalog'
import { LEVELS } from '@/prakrit/components/practice/practice-types'

import {
    TOPIC_BY_ID,
    VIKALP_PARAM,
    practiceHref,
    practiceHubHref,
    topicQuery
} from '@/prakrit/components/practice/practice-topics'

interface SetPickerProps {
    mode: PracticeMode
    level: Level
    topics: string[]
    vikalp: boolean
    /** Where a locked set's button leads. */
    lockedHref?: string
}

const UNAVAILABLE_TEXT: Record<Unavailable, { hi: string, en: string }> = {
    no_verified: { hi: 'इस खेल के अभ्यास जल्द खुलेंगे।', en: 'This game opens soon.' },
    no_recordings: { hi: 'सुनने के अभ्यास के लिए रिकॉर्डिंग या डिवाइस की आवाज़ चाहिए।', en: 'Listening needs a recording or your device’s voice.' },
    no_stories: { hi: 'कथा क्रम के लिए कथाएँ जल्द खुलेंगी।', en: 'Stories for this game open soon.' },
    no_focus: { hi: 'इस शब्द के अभ्यास अभी नहीं हैं।', en: 'There is nothing to practise for this word yet.' },
    no_topic: { hi: 'इन विषयों के लिए अभी कोई अभ्यास नहीं; विषय बदलकर देखें।', en: 'Nothing to practise for these topics yet; try other topics.' },
    needs_plan: { hi: 'यह सेट प्रीमियम में है।', en: 'This set is Premium.' }
}

const setHref = (mode: PracticeMode, level: Level, topics: string[], vikalp: boolean, set: number): string => (
    `${practiceHref(mode, level, topics, vikalp)}&set=${set}`
)

const tone = (record: SetRecord | undefined): string => {
    if (!record) return 'border-border bg-card text-foreground hover:border-saffron/60 hover:bg-saffron/8'

    const share = record.best / Math.max(1, record.size)

    if (share >= 0.8) return 'border-success/50 bg-success/12 text-success hover:bg-success/18'
    if (share >= 0.5) return 'border-saffron/60 bg-saffron/12 text-saffron-strong hover:bg-saffron/18'

    return 'border-terracotta/50 bg-terracotta/8 text-terracotta hover:bg-terracotta/12'
}

export const SetPicker: FC<SetPickerProps> = ({ mode, level, topics, vikalp, lockedHref = '/pricing' }): ReactNode => {
    const { mode: viewMode, hydrated } = usePrefs()
    const { voiceSupported, voiceEnabled } = useAudio()
    const kids = viewMode === 'kids'
    const voice = mode === 'shravan' && voiceSupported && voiceEnabled
    const [bank, setBank] = useState<BankInfo | null>(null)
    const [unavailable, setUnavailable] = useState<Unavailable | null>(null)
    const [failed, setFailed] = useState(false)
    const { records, loaded } = useSetProgress(bank?.key ?? null)
    const { access } = useSession()
    const info = modeInfo(mode)
    // The server says which sets are locked for this visitor; it is asked again when the session (a new trial) changes.
    const lockedFrom = bank?.lockedFrom ?? null

    useEffect(() => {
        if (!hydrated) return

        let cancelled = false
        const params = new URLSearchParams({ mode, level, view: kids ? 'kids' : 'adults' })

        if (topics.length > 0) params.set('topic', topicQuery(topics))
        if (vikalp) params.set(VIKALP_PARAM, '1')
        if (voice) params.set('voice', '1')

        setBank(null)
        setUnavailable(null)
        setFailed(false)

        fetch(`/api/practice/bank?${params.toString()}`, { cache: 'no-store' })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`)

                return res.json() as Promise<BankResponse>
            })
            .then(data => {
                if (cancelled) return
                if ('unavailable' in data) setUnavailable(data.unavailable)
                else setBank(data.bank)
            })
            .catch(() => {
                if (!cancelled) setFailed(true)
            })

        return () => {
            cancelled = true
        }
    }, [hydrated, mode, level, kids, topics, vikalp, voice, access.full])

    const done = useMemo(() => Object.keys(records).length, [records])
    const isLocked = (set: number): boolean => lockedFrom !== null && set >= lockedFrom
    const nextSet = useMemo(() => {
        if (!bank) return 1

        for (let s = 1; s <= bank.sets; s += 1) {
            if (!records[String(s)] && !isLocked(s)) return s
        }

        return 1
        // isLocked only reads lockedFrom.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bank, records, lockedFrom])
    const [randomSet, setRandomSet] = useState(1)

    useEffect(() => {
        if (!bank) return

        const open = lockedFrom === null ? bank.sets : Math.max(1, Math.min(bank.sets, lockedFrom - 1))

        setRandomSet(1 + Math.floor(Math.random() * open))
    }, [bank, lockedFrom])

    const topicLabels = topics.map(id => TOPIC_BY_ID[id]).filter(Boolean)

    return (
        <section aria-labelledby='sets-title' className='mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pb-16 pt-6 sm:px-6 sm:pt-10'>
            <nav aria-label='Breadcrumb' className='-ml-1'>
                <Link
                    href={practiceHubHref(topics, vikalp)}
                    className='group inline-flex min-h-tap items-center gap-1.5 rounded-full px-1 text-sm text-muted-foreground transition-colors hover:text-saffron-strong'
                >
                    <ArrowLeft className='size-4 transition-transform group-hover:-translate-x-0.5' aria-hidden='true' />
                    <span lang='hi' className='hindi'>सभी खेल</span>
                    <span aria-hidden='true'>·</span>
                    <span lang='en'>All games</span>
                </Link>
            </nav>

            <header className='flex flex-col gap-2'>
                <Eyebrow>अभ्यास सेट</Eyebrow>
                <h1 id='sets-title' className='flex flex-col gap-1'>
                    <span lang='hi' className='text-[2rem] font-bold leading-tight sm:text-[2.5rem]'>{info.name.hi}</span>
                    <span lang='en' className='font-sans text-lg font-medium text-muted-foreground'>{info.name.en}</span>
                </h1>
                <Bi hi={kids ? info.kidsLine.hi : info.line.hi} en={kids ? info.kidsLine.en : info.line.en} className='max-w-3xl gap-1 text-base leading-relaxed text-muted-foreground' />
            </header>

            <div className='flex flex-col gap-3 rounded-3xl border border-border bg-card p-4 shadow-card sm:p-5'>
                <div role='group' aria-label='स्तर · Level' className='flex flex-wrap gap-2'>
                    {LEVELS.map(l => (
                        <Link
                            key={l}
                            href={practiceHref(mode, l, topics, vikalp)}
                            aria-current={l === level ? 'page' : undefined}
                            className={cn(
                                'inline-flex min-h-tap items-center gap-1.5 rounded-full border px-4 text-sm transition-colors',
                                l === level ? 'border-saffron bg-saffron font-semibold text-primary-foreground' : 'border-border bg-background hover:border-saffron/60'
                            )}
                        >
                            <span lang='hi' className='hindi'>{LEVEL_INFO[l].numeral}. {LEVEL_INFO[l].name.hi}</span>
                            <span lang='en' data-ex='en' className='opacity-80'>{LEVEL_INFO[l].name.en}</span>
                        </Link>
                    ))}
                </div>
                <p className='text-sm text-muted-foreground'>
                    <span lang='hi' data-ex='hi' className='hindi'>{info.levels[level].hi}</span>
                    <span aria-hidden='true' className='ex-sep'> · </span>
                    <span lang='en' data-ex='en'>{info.levels[level].en}</span>
                </p>
                {(topicLabels.length > 0 || vikalp) && (
                    <ul className='flex flex-wrap gap-1.5' aria-label='चुने गए विषय · Chosen topics'>
                        {topicLabels.map(t => (
                            <li key={t.id} className='rounded-full bg-secondary px-3 py-1 text-xs font-medium'>
                                <span lang='hi' className='hindi'>{t.full.hi}</span>
                                <span lang='en' data-ex='en' className='text-muted-foreground'> · {t.full.en}</span>
                            </li>
                        ))}
                        {vikalp && (
                            <li className='rounded-full bg-terracotta/10 px-3 py-1 text-xs font-medium text-terracotta'>
                                <span lang='hi' className='hindi'>विकल्प सहित</span>
                                <span lang='en' data-ex='en'> · variants included</span>
                            </li>
                        )}
                    </ul>
                )}
            </div>

            {failed && (
                <Notice tone='danger'>
                    <Bi hi='अभ्यास लोड नहीं हो सके। पृष्ठ फिर से खोलिए।' en='The exercises could not load. Please open the page again.' />
                </Notice>
            )}

            {unavailable && (
                <Notice>
                    <Bi hi={UNAVAILABLE_TEXT[unavailable].hi} en={UNAVAILABLE_TEXT[unavailable].en} />
                </Notice>
            )}

            {!bank && !failed && !unavailable && (
                <div className='flex min-h-48 items-center justify-center gap-3 text-muted-foreground' role='status'>
                    <LoaderCircle className='size-6 animate-spin text-saffron-strong' aria-hidden='true' />
                    <Bi hi='अभ्यास तैयार हो रहे हैं…' en='Getting the exercises ready…' layout='inline' />
                </div>
            )}

            {bank && (
                <>
                    <dl className='grid grid-cols-3 gap-3'>
                        {[
                            { hi: 'अभ्यास', en: 'exercises', value: bank.total },
                            { hi: 'सेट', en: `sets of ${bank.setSize}`, value: bank.sets },
                            { hi: 'खेले गए', en: 'sets played', value: loaded ? done : null }
                        ].map(stat => (
                            <div key={stat.en} className='flex flex-col gap-0.5 rounded-2xl border border-border bg-card px-4 py-3 shadow-card'>
                                <dt className='order-2 text-xs text-muted-foreground'>
                                    <span lang='hi' className='hindi'>{stat.hi}</span>
                                    <span lang='en' data-ex='en'> · {stat.en}</span>
                                </dt>
                                <dd className='order-1 font-sans text-2xl font-bold tabular-nums sm:text-3xl'>
                                    {stat.value === null ? '…' : (
                                        <>
                                            <span lang='hi' className='hindi' aria-hidden='true'>{hiDigits(stat.value)}</span>
                                            <span className='sr-only'>{stat.value}</span>
                                        </>
                                    )}
                                </dd>
                            </div>
                        ))}
                    </dl>

                    <div className='flex flex-wrap gap-3'>
                        <Link href={setHref(mode, level, topics, vikalp, nextSet)} className={cn(PRIMARY_BUTTON, 'min-h-12 px-6 text-base')}>
                            <Play className='size-5' aria-hidden='true' />
                            <span lang='hi' className='hindi'>सेट {hiDigits(nextSet)} शुरू करें</span>
                            <span aria-hidden='true'>·</span>
                            <span lang='en'>Start set {nextSet}</span>
                        </Link>
                        <Link href={setHref(mode, level, topics, vikalp, randomSet)} className={cn(SECONDARY_BUTTON, 'min-h-12')}>
                            <Dices className='size-5' aria-hidden='true' />
                            <span lang='hi' className='hindi'>कोई भी सेट</span>
                            <span aria-hidden='true'>·</span>
                            <span lang='en'>Random set</span>
                        </Link>
                    </div>

                    {lockedFrom !== null && lockedFrom <= bank.sets && (
                        <Notice icon={<Lock className='size-4' aria-hidden='true' />}>
                            <Bi
                                hi={`पहले ${hiDigits(lockedFrom - 1)} सेट मुफ़्त हैं। सभी ${hiDigits(bank.sets)} सेट प्रीमियम में हैं।`}
                                en={`The first ${lockedFrom - 1} sets are free. All ${bank.sets} sets come with Premium.`}
                            />
                            <Link href={lockedHref} className='mt-1 inline-flex min-h-tap items-center text-sm font-semibold text-saffron-strong hover:underline'>
                                <span lang='hi' className='hindi'>प्रीमियम देखें</span>
                                <span aria-hidden='true' className='px-1'>·</span>
                                <span lang='en'>See Premium</span>
                            </Link>
                        </Notice>
                    )}

                    <div className='flex flex-col gap-3'>
                        <h2 className='flex flex-wrap items-baseline gap-x-2'>
                            <span lang='hi' className='text-xl font-bold'>सभी सेट</span>
                            <span lang='en' className='font-sans text-sm text-muted-foreground'>Every set · questions come in a new random order each time</span>
                        </h2>
                        <ol className='grid grid-cols-5 gap-2 sm:grid-cols-8 lg:grid-cols-10'>
                            {Array.from({ length: bank.sets }, (_, i) => i + 1).map(set => {
                                const record = records[String(set)]
                                const locked = isLocked(set)
                                const perfect = record && record.best === record.size
                                const label = locked
                                    ? `सेट ${set}, प्लान से खुलेगा · Set ${set}, needs a plan`
                                    : record
                                        ? `सेट ${set}: सर्वश्रेष्ठ ${record.best} / ${record.size} · Set ${set}: best ${record.best} of ${record.size}`
                                        : `सेट ${set}, अभी नहीं खेला · Set ${set}, not played yet`

                                return (
                                    <li key={set}>
                                        <Link
                                            href={locked ? lockedHref : setHref(mode, level, topics, vikalp, set)}
                                            aria-label={label}
                                            className={cn(
                                                'relative flex aspect-square flex-col items-center justify-center gap-0.5 rounded-2xl border text-center transition-colors',
                                                locked ? 'border-dashed border-border bg-surface-muted/60 text-muted-foreground hover:border-saffron/60' : tone(record),
                                                set === nextSet && !record && !locked && 'ring-2 ring-saffron ring-offset-2 ring-offset-background'
                                            )}
                                        >
                                            <span aria-hidden='true' className='font-sans text-lg font-bold tabular-nums leading-none'>{set}</span>
                                            {locked ? (
                                                <Lock className='size-3.5' aria-hidden='true' />
                                            ) : record ? (
                                                <span aria-hidden='true' className='inline-flex items-center gap-0.5 text-[0.6875rem] font-semibold tabular-nums leading-none'>
                                                    {perfect ? <Star className='size-3 fill-current' /> : <Check className='size-3' />}
                                                    {record.best}/{record.size}
                                                </span>
                                            ) : null}
                                        </Link>
                                    </li>
                                )
                            })}
                        </ol>
                    </div>
                </>
            )}
        </section>
    )
}
